import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '~/data/company/queryKeys';

import { queryKeysCompanyAccount } from '../queryKeys';

import { deleteCompanyAccountSupplierLetter } from './deleteCompanyAccountSupplierLetter';

/**
 * Custom hook for clearing the CA-level supplier letter.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/delete_company_account_supplier_letter_vadmin_company_accounts__company_account_id__supplier_letter_delete
 */
export const useMutationDeleteCompanyAccountSupplierLetter = (
  options?: Omit<
    UseMutationOptions<void, Error, { companyAccountId: UUID }>,
    'mutationFn' | 'onSuccess'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { companyAccountId: UUID }>({
    async mutationFn({ companyAccountId }) {
      return deleteCompanyAccountSupplierLetter(companyAccountId);
    },
    async onSuccess(_data, { companyAccountId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysCompanyAccount.getSupplierLetter(companyAccountId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysCompany.getSupplierLetters(),
        }),
      ]);
    },
    ...options,
  });
};
